





































































import { DescriptionListCard, FormCard } from '@/app/components';
import LineChart from '@/app/components/charts/LineChart.vue';
import { S } from '@/app/utilities';
import { useAxios } from '@/app/composable';
import { computed, defineComponent, PropType, ref } from '@vue/composition-api';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { SelfBuildingSquareSpinner } from 'epic-spinners';
import { isNil, pluck } from 'ramda';
import apexchart from 'vue-apexcharts';
import { MetricsAPI } from '../../api';
import { Asset } from '../../types';
import { AssetHistoricalMetrics, HistoricalMetricsPayload } from '../../types/data-profiling.type';
import PeriodFilters from '../PeriodFilters.vue';
import HistoricalGraph from './HistoricalGraph.vue';

export default defineComponent({
    name: 'AssetHistoricalMetrics',
    components: {
        DescriptionListCard,
        FormCard,
        PeriodFilters,
        LineChart,
        SelfBuildingSquareSpinner,
        apexchart,
        HistoricalGraph,
    },
    props: {
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
        isStreaming: {
            type: Boolean,
            default: false,
        },
    },
    setup(props: any, { emit, root }: { emit: any; root: any }) {
        const { exec, loading } = useAxios(true);
        const lastExecutionsText = computed(() => (props.isStreaming ? 'Updates' : 'Executions'));
        const assetHistoricalMetrics = ref<AssetHistoricalMetrics | undefined>(undefined);
        const metricsQuery = ref<{
            startDate: Date | null;
            endDate: Date | null;
            limit: Number | null;
        }>();
        const fullSizeGraphs = ref<boolean>(false);

        const errorResponse = ref<{ message?: string; status: number } | undefined>();

        const fetchAssetHistoricalMetrics = () => {
            errorResponse.value = undefined;
            assetHistoricalMetrics.value = undefined;
            if (isNil(props.asset.id || !metricsQuery.value)) return;
            const payload: HistoricalMetricsPayload = {
                start: metricsQuery.value?.startDate ?? null,
                end: metricsQuery.value?.endDate ?? null,
                limit: metricsQuery.value?.limit ?? null,
            };
            exec(MetricsAPI.getAssetHistoricalMetrics(+props.asset.id, payload))
                .then((res: AxiosResponse<AssetHistoricalMetrics> | undefined) => {
                    if (!res) return;
                    assetHistoricalMetrics.value = res.data;
                })
                .catch((e) => {
                    if (e.response.status === 403) {
                        (root as any).$toastr.e(
                            'You do not have access to view the specific asset.',
                            'Access Forbidden!',
                        );
                        emit('back-to-assets');
                    } else if (e.response.status === 413) {
                        errorResponse.value = { status: 413 };
                    } else {
                        errorResponse.value = {
                            message: e?.response?.data?.message,
                            status: e.response.status,
                        };
                    }
                });
        };

        const obervationsAndMissingCellsGraph = computed(() => {
            if (!assetHistoricalMetrics.value?.results.length) return null;
            return {
                series: [
                    {
                        name: 'Observations',
                        data: pluck('numOfObservations', assetHistoricalMetrics.value.results),
                    },
                    {
                        name: 'Percentage of Missing Cells',
                        data: pluck('percentageOfMissingCells', assetHistoricalMetrics.value.results).map(
                            (percentage: number) => percentage * 100,
                        ),
                    },
                ],
                xaxis: {
                    categories: pluck('calculatedAt', assetHistoricalMetrics.value.results).map((v: string) =>
                        dayjs.utc(v).format('DD/MM/YYYY HH:mm'),
                    ),
                    title: { text: 'Date' },
                },
                yaxis: [
                    {
                        title: { text: 'Number of Observations' },
                        opposite: true,
                        labels: {
                            formatter: (value: any) => value?.toFixed(0),
                        },
                    },
                    {
                        title: { text: 'Percentage of Missing Cells' },
                        min: 0,
                        max: 100,
                        labels: {
                            formatter: (value: any) => (value ?? 0).toFixed(2) + '%',
                        },
                    },
                ],
                details: {
                    legend: { position: 'top' },
                    zoom: { enabled: true, allowMouseWheelZoom: false },
                    markers: assetHistoricalMetrics.value.results.length <= 20 ? 6 : 0,
                    stroke: { width: assetHistoricalMetrics.value.results.length === 1 ? 0 : 2 },
                },
            };
        });

        const memorySizeGraph = computed(() => {
            if (!assetHistoricalMetrics.value?.results.length) return null;

            return {
                series: [
                    {
                        name: 'Memory Size',
                        data: pluck('memorySize', assetHistoricalMetrics.value.results),
                    },
                ],
                options: {
                    chart: {
                        type: 'bar',
                    },
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            columnWidth: assetHistoricalMetrics.value.results.length > 5 ? '80%' : '30%',
                            borderRadius: 5,
                            borderRadiusApplication: 'end',
                        },
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    xaxis: {
                        categories: pluck('calculatedAt', assetHistoricalMetrics.value.results).map((v: string) =>
                            dayjs.utc(v).format('DD/MM/YYYY HH:mm'),
                        ),
                        title: { text: 'Date' },
                    },
                    yaxis: [
                        {
                            title: { text: 'Memory Size' },
                            labels: { formatter: (val: number) => S.bytesConvertToLargerUnit(val) },
                        },
                    ],
                    colors: ['#2f8aa1'],
                    details: {
                        legend: { position: 'top' },
                        zoom: { enabled: true, allowMouseWheelZoom: false },
                        markers: assetHistoricalMetrics.value.results.length <= 20 ? 6 : 0,
                    },
                },
            };
        });

        return {
            loading,
            metricsQuery,
            fullSizeGraphs,
            obervationsAndMissingCellsGraph,
            memorySizeGraph,
            assetHistoricalMetrics,
            lastExecutionsText,
            errorResponse,
            fetchAssetHistoricalMetrics,
        };
    },
});
